import { useState } from "react";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import LiveLister from "../../components/Lister/LiveLister";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { generateRemoteUrl } from "./Exercices.function";
import ExerciseCreationModal from "./components/CreationModal";

const Exercices = () => {
  const { language } = useSession();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [detailedView, setDetailedView] = useState(false);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const columns = [
    { header: translate(language, "PAGES.EXERCISES.ID"), key: "id", sortable: true },
    { header: translate(language, "PAGES.EXERCISES.NAME"), key: "name", sortable: true },
    { header: translate(language, "PAGES.EXERCISES.DIFFICULTY"), key: "difficulty", sortable: true },
    {
      header: translate(language, "PAGES.EXERCISES.STATUS"),
      key: "is_deprecated",
      render: (exercise: any) =>
        exercise.is_deprecated ? (
          <span className="badge bg-secondary">{translate(language, "PAGES.EXERCISES.STATUS_OPTIONS.DEPRECATED")}</span>
        ) : (
          <span className="badge bg-success">{translate(language, "PAGES.EXERCISES.STATUS_OPTIONS.ACTIVE")}</span>
        ),
    },
    {
      header: translate(language, "PAGES.EXERCISES.OWNER"),
      key: "owner",
      render: (exercise: any) => (
        <span
          className="clickable text-primary"
          onClick={(event) => completeNavigate(event, navigate, `/user/${exercise.owner.id}`)}
        >
          {exercise.owner.full_name}
        </span>
      ),
    },
    {
      header: translate(language, "PAGES.EXERCISES.ORIGIN_MODULE"),
      key: "origin_module",
      render: (exercise: any) =>
        exercise.origin_module ? (
          <span
            className="clickable text-primary"
            onClick={(event) => completeNavigate(event, navigate, `/module/${exercise.origin_module.id}`)}
          >
            {exercise.origin_module.name}
          </span>
        ) : (
          "—"
        ),
    },
    ...(detailedView
      ? [
          { header: translate(language, "PAGES.EXERCISES.DESCRIPTION"), key: "description" },
          { header: translate(language, "PAGES.EXERCISES.DURATION"), key: "duration", sortable: true },
          { header: translate(language, "PAGES.EXERCISES.TYPE"), key: "type", sortable: true },
          { header: translate(language, "PAGES.EXERCISES.LANGUAGE"), key: "language", sortable: true },
        ]
      : []),
    {
      header: translate(language, "PAGES.EXERCISES.CREATED_AT"),
      key: "dt_created",
      sortable: true,
      render: (exercise: any) => <span>{formatDateHumanly(language, exercise.dt_created)}</span>,
    },
    {
      header: translate(language, "PAGES.EXERCISES.UPDATED_AT"),
      key: "dt_updated",
      sortable: true,
      render: (exercise: any) => (
        <span>{exercise.dt_updated ? formatDateHumanly(language, exercise.dt_updated) : "N/A"}</span>
      ),
    },
  ];

  return (
    <div className="m-5 position-relative">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex w-fit align-items-center">
          <h2 className="mb-0 me-3">{translate(language, "PAGES.EXERCISES.TITLE")}</h2>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </div>
      </div>
      <div className="d-flex justify-content-end mb-3">
        <div className="me-2 d-flex align-items-center" style={{ width: 200 }}>
          <DstButton
            btnClass="w-100 h-100 d-flex align-items-center justify-content-center"
            btnImageBefore={
              <Form.Check
                type="switch"
                className="check-box s-bold me-2"
                id="detailed-view-exercises-switch"
                checked={detailedView}
                onChange={() => setDetailedView(!detailedView)}
              />
            }
            value={translate(language, "PAGES.EXERCISES.DETAILED_VIEW")}
            variant="light"
            btnHeight="50px"
            clickFunction={() => setDetailedView(!detailedView)}
          />
        </div>
        <div className="d-flex align-items-center" style={{ width: 200 }}>
          <DstButton
            btnClass="w-100 h-100 d-flex align-items-center justify-content-center"
            btnImageBefore={
              <Form.Check
                type="switch"
                className="check-box s-bold me-2"
                id="show-only-active-exercises-switch"
                checked={showOnlyActive}
                onChange={() => setShowOnlyActive(!showOnlyActive)}
              />
            }
            value={translate(language, "PAGES.EXERCISES.SHOW_ONLY_ACTIVE")}
            variant="light"
            btnHeight="50px"
            clickFunction={() => setShowOnlyActive(!showOnlyActive)}
          />
        </div>
        <div className="ms-2 d-flex align-items-center">
          <DstButton
            value={translate(language, "PAGES.EXERCISES.BUTTON_ADD_EXERCISE")}
            btnImageBefore={<span className="me-2 dst-icon-plus" />}
            clickFunction={() => setShowCreationModal(true)}
          />
        </div>
      </div>

      <LiveLister
        key={refreshKey}
        remote={generateRemoteUrl(searchQuery, showOnlyActive)}
        remoteKey="exercises"
        columns={columns}
        additionalButton={(item: any) => (
          <span
            className="clickable xs-regular"
            onClick={(event) => completeNavigate(event, navigate, `/exercise/${item.id}`)}
          >
            <span className="dst-icon-arrow-narrow-right m-regular" />
          </span>
        )}
        showHeader
        pagination
        itemsPerPage={20}
      />
      <ExerciseCreationModal
        language={language}
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        onSuccess={() => {
          setShowCreationModal(false);
          setRefreshKey((previous) => previous + 1);
        }}
      />
    </div>
  );
};

export default Exercices;
